import styled from 'styled-components';
import { monserratSemiBold, robotoRegular } from '@habitech/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f3f3f3;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Navbar
// ─────────────────────────────────────────────────────────────────────────────
export const NavBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const NavBrand = styled.div``;

export const NavTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #5b21b6;
  font-family: ${monserratSemiBold};
`;

export const NavSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
  font-family: ${robotoRegular};

  span.dot {
    margin: 0 8px;
    color: #d1d5db;
  }

  span.email {
    color: #6b7280;
  }
`;

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const EnvBadge = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color + '20'};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color + '40'};
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 600;
  font-family: ${robotoRegular};
`;

export const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${robotoRegular};

  &:hover {
    border-color: #dc2626;
    color: #dc2626;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Section header
// ─────────────────────────────────────────────────────────────────────────────
export const SectionWrapper = styled.div`
  margin-bottom: 1.5rem;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  font-family: ${monserratSemiBold};
`;

export const SectionSubtitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #9ca3af;
  font-family: ${robotoRegular};
`;

export const SectionDivider = styled.div`
  margin-top: 0.75rem;
  height: 2px;
  background: linear-gradient(90deg, #e5e7eb 0%, transparent 100%);
  border-radius: 2px;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Cards (shared)
// ─────────────────────────────────────────────────────────────────────────────
export const CardWrapper = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 1.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.07);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.1);
  }
`;

export const CardIcon = styled.div<{ $color?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $color }) => ($color ? $color + '18' : '#7c3aed18')};
  border: 1px solid ${({ $color }) => ($color ? $color + '30' : '#7c3aed30')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  line-height: 1;
  flex-shrink: 0;
`;

export const CardTitle = styled.h5`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  font-family: ${monserratSemiBold};
`;

export const CardDescription = styled.p`
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #6b7280;
  font-family: ${robotoRegular};
`;

export const CardButton = styled.button`
  margin-top: auto;
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  width: 100%;
  font-family: ${monserratSemiBold};

  &:hover {
    opacity: 0.85;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// External tool card extras
// ─────────────────────────────────────────────────────────────────────────────
export const ExternalCardWrapper = styled(CardWrapper)`
  gap: 1rem;
`;

export const ToolCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export const EnvBadgeSmall = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color + '20'};
  color: ${({ $color }) => $color};
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid ${({ $color }) => $color + '40'};
`;

export const UrlBox = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #374151;
  word-break: break-all;
  border: 1px solid #e5e7eb;
  font-family: ${robotoRegular};

  span.label {
    color: #9ca3af;
    font-weight: 600;
  }
`;

export const SsoMethodLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: ${robotoRegular};
`;

export const OpeningCard = styled.div`
  background: white;
  border-radius: 16px;
  border: 2px solid #7c3aed;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  height: 100%;
  min-height: 200px;
`;

export const OpeningLabel = styled.span`
  color: #5b21b6;
  font-weight: 600;
  font-size: 14px;
  font-family: ${monserratSemiBold};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Banners & footer
// ─────────────────────────────────────────────────────────────────────────────
export const LocalhostBanner = styled.div`
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  padding: 12px 18px;
  margin-bottom: 1.5rem;
  font-size: 13px;
  color: #92400e;
  font-family: ${robotoRegular};

  strong {
    font-family: ${monserratSemiBold};
  }
`;

export const FooterBox = styled.div`
  margin-top: 3rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;

  strong {
    color: #374151;
    font-family: ${monserratSemiBold};
  }

  ol {
    margin: 0.75rem 0 0;
    padding-left: 1.5rem;
    line-height: 2;
    font-family: ${robotoRegular};
  }
`;
